import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  Injector,
  inject,
  input,
  OnDestroy,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import * as pdfjsLib from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { ButtonComponent } from '../../input/button/button.component';
import { ICONS, LOADING_SERVICE_TOKEN } from '@aksia/infrastructure';
import { LabelComponent } from '../label/label.component';
import { UiLoadingDirective } from '../../../directives/ui/ui-loading.directive';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/assets/pdf.worker.min.mjs';

@Component({
  selector: 'pdf-viewer',
  imports: [ButtonComponent, LabelComponent, UiLoadingDirective],
  templateUrl: './pdf-viewer.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: grid;
      grid-template-rows: auto 1fr;
      width: 100%;
      height: 100%;
      overflow: hidden;
    }
    [uipdfviewercanvas] {
      overflow: auto;
    }
  `,
})
export class PdfViewerComponent implements OnDestroy {
  //#region Injections

  private loadingService = inject(LOADING_SERVICE_TOKEN);

  //#endregion

  //#region Constants

  ICONS = ICONS;

  //endregion

  //#region Host Bindings

  @HostBinding('attr.uicontrol')
  get uicontrol() {
    return '';
  }

  //#endregion

  //#region Inputs

  src = input<string | ArrayBuffer | Uint8Array>();
  page = input<number>(1);
  scale = input<number>(1.5);

  //#endregion

  //#region Template Refs

  @ViewChild('pdfCanvas', { static: false })
  private canvasRef!: ElementRef<HTMLCanvasElement>;

  //#endregion

  //#region State

  protected currentPage: WritableSignal<number> = signal(this.page());
  protected totalPages: WritableSignal<number> = signal(0);
  protected currentScale: WritableSignal<number> = signal(this.scale());

  //#endregion

  //#region Computed

  protected scaleLabel = computed(
    () => `${Math.round(this.currentScale() * 100)}%`,
  );

  //#endregion

  //#region Private

  private pdfDoc: PDFDocumentProxy | null = null;
  private loadingTask: pdfjsLib.PDFDocumentLoadingTask | null = null;
  private renderTask: pdfjsLib.RenderTask | null = null;
  private readonly injector = inject(Injector);

  //#endregion

  constructor() {
    afterNextRender(() => {
      this.loadingService.addLoadRequest('PDF File', { type: 'area' });
      effect(
        () => {
          const src = this.src();
          if (src) {
            this.loadDocument(src);
          }
        },
        { injector: this.injector },
      );

      effect(
        () => {
          this.currentPage();
          this.currentScale();
          if (this.pdfDoc) {
            this.renderPage(this.currentPage());
          }
        },
        { injector: this.injector },
      );
    });
  }

  //#region Methods

  protected prevPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((p) => p - 1);
    }
  }

  protected nextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((p) => p + 1);
    }
  }

  protected zoomIn() {
    if (this.currentScale() < 3) {
      this.currentScale.update((s) => Math.min(3, +(s + 0.25).toFixed(2)));
    }
  }

  protected zoomOut() {
    if (this.currentScale() > 0.5) {
      this.currentScale.update((s) => Math.max(0.5, +(s - 0.25).toFixed(2)));
    }
  }

  private async loadDocument(src: string | ArrayBuffer | Uint8Array) {
    await this.loadingTask?.destroy();
    this.pdfDoc = null;

    this.loadingTask = pdfjsLib.getDocument(
      typeof src === 'string' ? { url: src } : { data: src },
    );
    this.pdfDoc = await this.loadingTask.promise;
    this.totalPages.set(this.pdfDoc.numPages);
    this.currentPage.set(this.page());
    this.loadingService.setLoadResponse('PDF File');
    await this.renderPage(this.currentPage());
  }

  private async renderPage(pageNum: number) {
    if (!this.pdfDoc || !this.canvasRef) return;

    this.renderTask?.cancel();

    const page = await this.pdfDoc.getPage(pageNum);
    const viewport = page.getViewport({ scale: this.currentScale() });
    const canvas = this.canvasRef.nativeElement;
    const context = canvas.getContext('2d')!;
    canvas.width = viewport.width;
    canvas.height = viewport.height;

    this.renderTask = page.render({ canvasContext: context, canvas, viewport });
    await this.renderTask.promise;
  }

  //#endregion

  ngOnDestroy() {
    this.renderTask?.cancel();
    void this.loadingTask?.destroy();
  }
}
